import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Modal, Spinner, Button } from 'react-bootstrap';
import BootstrapCard from 'react-bootstrap/Card';
import Card from '../../../components/Card';
import { apiHandler } from '../../../api/apihandler';
import { ANNOUNCEMENT_LIST_API } from '../../../api/constants';

const formatDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// Full "view all" list, reached from the AnnouncementBanner's "View all"
// link - the banner itself only ever shows one at a time (rotating), this
// is the complete audience-filtered list from the same
// ANNOUNCEMENT_LIST_API (see harmonydaycare-api's AnnouncementController).
const SchoolAnnouncements = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiHandler(ANNOUNCEMENT_LIST_API, 'POST', {});
        if (cancelled) return;
        // apiHandler() never throws - a failed request resolves with the raw
        // axios error instead, so without this check a 404/network failure
        // silently looked identical to "no announcements".
        if (res?.isAxiosError || (res?.response && res?.data === undefined)) {
          setError('Failed to load announcements.');
          return;
        }
        const list = res?.data?.data?.announcements;
        setAnnouncements(Array.isArray(list) ? list : []);
      } catch (err) {
        if (!cancelled) setError('Failed to load announcements.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div id="content-page" className="content-page">
      <Container>
        <Row>
          <Col sm="12">
            <Card>
              <Card.Header>
                <div className="header-title">
                  <h4 className="card-title">
                    <i className="material-symbols-outlined me-2" style={{ verticalAlign: 'middle' }}>campaign</i>
                    Announcements
                  </h4>
                  <p className="text-muted mb-0">Everything currently announced for you</p>
                </div>
              </Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" role="status" style={{ color: '#6f42c1' }}>
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <p className="mt-3 text-muted mb-0">Loading announcements...</p>
                  </div>
                ) : error ? (
                  <div className="text-center py-5">
                    <i className="material-symbols-outlined" style={{ fontSize: '48px', color: '#dc3545' }}>error</i>
                    <p className="text-danger mt-2 mb-0">{error}</p>
                  </div>
                ) : announcements.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="material-symbols-outlined" style={{ fontSize: '64px', color: '#ccc' }}>campaign</i>
                    <p className="mt-3 text-muted mb-0">No announcements right now.</p>
                  </div>
                ) : (
                  <Row className="g-3">
                    {announcements.map((announcement) => (
                      <Col xs="12" key={announcement.id}>
                        <BootstrapCard
                          className="shadow-sm"
                          role="button"
                          onClick={() => setSelected(announcement)}
                          style={{ cursor: 'pointer', border: '1px solid #eef0f3', borderRadius: '12px' }}
                        >
                          <BootstrapCard.Body className="d-flex align-items-start gap-3">
                            <div
                              className="d-flex align-items-center justify-content-center text-white flex-shrink-0"
                              style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#6f42c1' }}
                            >
                              <i className="material-symbols-outlined" style={{ fontSize: '20px' }}>campaign</i>
                            </div>
                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                              <div
                                className="text-dark"
                                style={{
                                  fontSize: '14px',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                }}
                              >
                                {announcement.announcement_details}
                              </div>
                              {(formatDate(announcement.start_date) || formatDate(announcement.end_date)) && (
                                <div className="text-muted small mt-1">
                                  {formatDate(announcement.start_date) && <>From {formatDate(announcement.start_date)} </>}
                                  {formatDate(announcement.end_date) && <>to {formatDate(announcement.end_date)}</>}
                                </div>
                              )}
                            </div>
                            <i className="material-symbols-outlined text-muted flex-shrink-0">chevron_right</i>
                          </BootstrapCard.Body>
                        </BootstrapCard>
                      </Col>
                    ))}
                  </Row>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal show={!!selected} onHide={() => setSelected(null)} centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center gap-2">
            <i className="material-symbols-outlined" style={{ color: '#6f42c1' }}>campaign</i>
            Announcement
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {selected && (
            <>
              <p style={{ whiteSpace: 'pre-wrap', fontSize: '14px' }}>{selected.announcement_details}</p>
              {(formatDate(selected.start_date) || formatDate(selected.end_date)) && (
                <div className="text-muted small mb-2">
                  {formatDate(selected.start_date) && <>From {formatDate(selected.start_date)} </>}
                  {formatDate(selected.end_date) && <>to {formatDate(selected.end_date)}</>}
                </div>
              )}
              {selected.link && selected.link_text && (
                <a href={selected.link} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-primary mt-2">
                  {selected.link_text}
                </a>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelected(null)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default SchoolAnnouncements;
